import React, { useContext, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import SingleBaseLocationSelect from '../../../components/SingleBaseLocationSelect'
import {
  Archive,
  ArrowBack,
  AssignmentTurnedIn,
  DriveEta,
  Inventory2,
  LocalPharmacy,
  Person,
  QrCode2,
  Storefront,
  SwapHoriz,
  VpnKey,
  WarningAmber,
} from '@mui/icons-material'
import ReassignBag from './ReassignBag'
import { useNavigate } from 'react-router-dom'
import QRCodePopup from '../../../components/QRCode/QRCodePopup'
import ArchiveLocationPopup from './ArchiveLocationPopup'

const StatChip = ({ icon: Icon, label, tone = '#242F40' }) => (
  <Chip
    size="small"
    icon={<Icon />}
    label={label}
    sx={{
      borderRadius: 1,
      color: tone,
      fontWeight: 850,
      backgroundColor: `${tone}12`,
      border: `1px solid ${tone}24`,
      '& .MuiChip-icon': { color: tone },
    }}
  />
)

const DetailRow = ({ label, value, icon: Icon }) => {
  if (!value) return null

  return (
    <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
      {Icon && <Icon fontSize="small" sx={{ color: '#64748B' }} />}
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={850} noWrap>
        {value}
      </Typography>
    </Stack>
  )
}

const LocationBanner = ({
  location = {},
  setEnterDrugsToStore,
  contentSummary = [],
  contents = [],
  loadPageAPI,
}) => {
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const [openMoveBag, setOpenMoveBag] = useState(false)
  const [openQR, setOpenQR] = useState(false)
  const [openArchive, setOpenArchive] = useState(false)
  const [savingBase, setSavingBase] = useState(false)

  const isBag = location?.type !== 'Medicine Store'

  // Every store and bag belongs to a base location; changed inline here.
  const handleBaseLocationChange = async (base) => {
    if (!base?._id || base._id === location?.baseLocation?._id) return
    setSavingBase(true)
    try {
      const res = await API.put(
        `/medsmx/location/${location._id}/base-location`,
        { baseLocation: base._id },
      )
      if (res.status === 200) {
        setToast?.({ message: `Base location set to ${base.name}` })
        loadPageAPI?.()
      } else {
        setToast?.({
          message: res.data?.error || 'Failed to change base location',
          severity: 'error',
        })
      }
    } catch (e) {
      console.error(e)
      setToast?.({
        message: 'Failed to change base location',
        severity: 'error',
      })
    } finally {
      setSavingBase(false)
    }
  }
  const totalQuantity = contentSummary.reduce(
    (total, medicine) => total + Number(medicine.qty || 0),
    0,
  )
  const controlledCount = contentSummary.filter(
    (medicine) => medicine.isControlled,
  ).length
  const lowStockCount = contentSummary.filter(
    (medicine) => Number(medicine.qty || 0) < Number(medicine.alarm || 0),
  ).length

  const assignedTo = useMemo(() => {
    if (location.assignedToVehicle) return location.assignedToVehicle.callSign
    if (location.assignedToEvent) return location.assignedToEvent.title
    if (location.assignedToStore) return location.assignedToStore.name
    if (location.assignedToEmployee) {
      return [
        location.assignedToEmployee?.userAccount?.firstName,
        location.assignedToEmployee?.userAccount?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
    }

    return isBag ? 'Unassigned' : null
  }, [isBag, location])

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid rgba(15,23,42,0.08)',
        backgroundColor: '#fff',
      }}
    >
      <Stack spacing={1.75}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
          spacing={1.5}
        >
          <Stack spacing={1} minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
              <Button
                variant="outlined"
                onClick={() => navigate('/medicines-management')}
                sx={{
                  minWidth: 36,
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  p: 0,
                }}
              >
                <ArrowBack fontSize="small" />
              </Button>

              <Box minWidth={0}>
                <Typography variant="h5" fontWeight={950} noWrap>
                  {location.name}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={location.type}
                    sx={{ borderRadius: 1, fontWeight: 850 }}
                  />
                  {!isBag && location.allowAdministration && (
                    <Tooltip title="Medicines can be administered straight from this store">
                      <Chip
                        size="small"
                        color="info"
                        label="Mobile store"
                        sx={{ borderRadius: 1, fontWeight: 850 }}
                      />
                    </Tooltip>
                  )}
                  {location.archived && (
                    <Chip
                      size="small"
                      color="error"
                      label="Archived"
                      sx={{ borderRadius: 1, fontWeight: 850 }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {isBag && (
                <>
                  <DetailRow
                    icon={Inventory2}
                    label="Bag"
                    value={location.number}
                  />
                  <DetailRow
                    icon={LocalPharmacy}
                    label="Level"
                    value={location.level}
                  />
                  <DetailRow
                    icon={Storefront}
                    label="Location"
                    value={assignedTo}
                  />
                  {location.assignedToVehicle && (
                    <DetailRow
                      icon={DriveEta}
                      label="Vehicle"
                      value={location.assignedToVehicle?.callSign}
                    />
                  )}
                  {location.assignedToEmployee && (
                    <DetailRow
                      icon={Person}
                      label="Checked out"
                      value={`${assignedTo} (${location.assignedToEmployee?.clinicalRole})`}
                    />
                  )}
                  <DetailRow
                    icon={VpnKey}
                    label="Seal"
                    value={location.tagNumber || 'Removed'}
                  />
                </>
              )}
            </Stack>
          </Stack>

          {!location.archived && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
              useFlexGap
            >
              <SingleBaseLocationSelect
                value={location.baseLocation}
                onChange={handleBaseLocationChange}
                size="small"
                disabled={savingBase}
                sx={{ minWidth: 220 }}
              />
              {isBag ? (
                <Button
                  startIcon={<SwapHoriz />}
                  variant="outlined"
                  onClick={() => setOpenMoveBag(true)}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
                >
                  Reassign
                </Button>
              ) : (
                <Button
                  startIcon={<Inventory2 />}
                  variant="outlined"
                  onClick={() => setEnterDrugsToStore(true)}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
                >
                  Add stock
                </Button>
              )}

              <Button
                startIcon={<AssignmentTurnedIn />}
                variant="contained"
                onClick={() =>
                  navigate(`/medicines-management/record-audit/${location._id}`)
                }
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#1E7F88',
                }}
              >
                Audit
              </Button>

              {isBag && (
                <Tooltip title="Staff bag check QR code">
                  <Button
                    variant="outlined"
                    onClick={() => setOpenQR(true)}
                    sx={{ minWidth: 38, borderRadius: 1.5, p: 1 }}
                  >
                    <QrCode2 fontSize="small" />
                  </Button>
                </Tooltip>
              )}

              {contentSummary.length === 0 && (
                <Tooltip title="Archive location">
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => setOpenArchive(true)}
                    sx={{ minWidth: 38, borderRadius: 1.5, p: 1 }}
                  >
                    <Archive fontSize="small" />
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>

        {location.archived ? (
          <Alert severity="error" variant="outlined">
            This medicines location has been archived.
          </Alert>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <StatChip icon={Inventory2} label={`${contents.length} batches`} />
            <StatChip
              icon={LocalPharmacy}
              label={`${totalQuantity} items`}
              tone="#1E7F88"
            />
            <StatChip
              icon={WarningAmber}
              label={`${lowStockCount} low stock`}
              tone={lowStockCount > 0 ? '#D97706' : '#16A34A'}
            />
            <StatChip
              icon={VpnKey}
              label={`${controlledCount} controlled`}
              tone={controlledCount > 0 ? '#DC2626' : '#64748B'}
            />
          </Stack>
        )}
      </Stack>

      <ArchiveLocationPopup
        open={openArchive}
        onClose={() => setOpenArchive(false)}
        locationID={location._id}
      />
      <ReassignBag
        open={openMoveBag}
        onClose={() => setOpenMoveBag(false)}
        location={location}
        loadPageAPI={loadPageAPI}
      />
      <QRCodePopup
        open={openQR}
        onClose={() => setOpenQR(false)}
        title={`QR Code for ${location.name}`}
        imageLabel={location.name}
        url={new URL(`${window.location.origin}/checkmedsbag/${location._id}`)}
      />
    </Paper>
  )
}

export default LocationBanner
